import qs from 'qs'
import getIn from 'lodash/get'
import fetch from '../../common/fetch'

const ONE_MINUTE = 1000 * 60
const BASE_RESPONSE_CACHE = { ttl: ONE_MINUTE }

export const isBasedOnWorkdayPositionManagementModel = businessUnit => {
  return getIn(businessUnit, ['connector_settings', 'workday', 'is_position_management_model'], false)
}

const api = {
  businessUnit: {
    get: (id, opts = { responseCache: BASE_RESPONSE_CACHE }) => fetch.get(`/api/v2/business_units/${id}/`, opts),
    /**
     * Note that this api only fetches the first paginated response and will not return more than 100 business units.
     * `list_paginated` should be used going forward
     * @param parent
     * @param minLevel
     * @param search
     * @param permission
     * @param lockParent
     * @param userRestrictedParent
     * @param structureAsTree
     * @param excludeSubLabels
     * @param additionalIds
     * @param controllerSignal
     * @returns {Promise<BusinessUnitsResponse>}
     */
    list: async ({
      parent,
      minLevel,
      search,
      permission,
      lockParent,
      userRestrictedParent = false,
      structureAsTree = false,
      excludeSubLabels = false,
      isDeleted = false,
      additionalIds,
      controllerSignal,
      responseCache = BASE_RESPONSE_CACHE,
      paginated = true
    } = {}) => {
      const queries = qs.stringify({
        is_deleted: isDeleted,
        search: search || undefined,
        parent: !search && parent ? parent : undefined,
        level: !search && !parent && minLevel != null ? minLevel : undefined,
        min_level: minLevel || undefined,
        permission,
        additional_ids: additionalIds || undefined,
        user_restricted_parent: userRestrictedParent || undefined,
        exclude_sub_labels: excludeSubLabels,
        paginated,
        offset: 0,
        limit: 200
      })

      const url = `/api/v2/business_units/?${queries}`
      const response = await fetch.get(url, { responseCache, signal: controllerSignal })
      const results = paginated ? response?.results : response

      if (lockParent?.lock) {
        return filterForRoot(results, lockParent)
      }

      if (structureAsTree) {
        return toBuTree(results)
      }
      return results
    },

    // this one is used by new BU picker
    list_paginated: async ({
      parent,
      minLevel,
      search,
      permission,
      userRestrictedParent = false,
      excludeSubLabels = false,
      include_path_segments = true, // only for bu picker (list_paginated)
      isDeleted = false,
      additionalIds,
      controllerSignal,
      responseCache = BASE_RESPONSE_CACHE,
      offset = 0,
      limit = 200
    } = {}) => {
      const queries = qs.stringify({
        is_deleted: isDeleted,
        search: search || undefined,
        parent: !search && parent ? parent : undefined,
        level: !search && !parent && minLevel != null ? minLevel : undefined,
        min_level: minLevel ?? undefined,
        permission,
        additional_ids: additionalIds || undefined,
        user_restricted_parent: userRestrictedParent || undefined,
        exclude_sub_labels: excludeSubLabels,
        include_path_segments,
        paginated: true,
        offset,
        limit
      })

      const url = `/api/v2/business_units/?${queries}`
      const response = await fetch.get(url, { responseCache, signal: controllerSignal })
      return response
    },
    /**
     * @param data
     * @returns {Promise<BusinessUnitResponse>}
     */
    add: data => fetch.post('/api/v2/business_units/', data),
    /**
     * @param id
     * @param data
     * @returns {Promise<BusinessUnitResponse>}
     */
    edit: (id, data) => fetch.put(`/api/v2/business_units/${id}/`, data),
    isWorkdayPositionManagementModel: async id => {
      const bu = await api.businessUnit.get(id)
      return isBasedOnWorkdayPositionManagementModel(bu)
    },
    /**
     * @returns {Promise<BusinessUnitConfig>}
     */
    config: () => fetch.get('/api/v2/business_units/config'),
    /**
     * @param {{ limit?: number, offset?: number }} queryParams
     * @returns {Promise<OffsetLimitPaginationResponse<Log>>}
     */
    getActivityLogs: queryParams => fetch.get('/api/v2/business_units/activity_log?' + qs.stringify(queryParams)),
    /**
     * @param id
     * @param data
     * @returns {Promise<BusinessUnitConfig>}
     */
    toggle: (id, data) => fetch.patch(`/api/v2/business_units/${id}/`, data)
  },
  businessUnitLevels: {
    /**
     * @param opts
     * @returns {Promise<BusinessUnitLevelResponse>}
     */
    list: (
      opts = {
        responseCache: BASE_RESPONSE_CACHE
      }
    ) => fetch.get('/api/v2/business_unit_levels/', opts),
    /**
     * @param data
     * @returns {Promise<BusinessUnitLevelResponse>}
     */
    addLevel: data => fetch.post('/api/v2/business_unit_levels/', data),
    /**
     * @param id
     * @param data
     * @returns {Promise<BusinessUnitLevelResponse>}
     */
    renameLevel: (id, data) => fetch.patch(`/api/v2/business_unit_levels/${id}`, data),
    /**
     * @param {{ limit?: number, offset?: number }} queryParams
     * @returns {Promise<OffsetLimitPaginationResponse<Log>>}
     */
    getActivityLogs: queryParams => fetch.get('/api/v2/business_unit_levels/activity_log?' + qs.stringify(queryParams))
  }
}

export default api

function filterForRoot(response, { lock, parents }) {
  const parent_ids = parents?.map(p => p.id) ?? []
  return response.filter(d => parent_ids.includes(lock) || d.id === lock)
}

export function toBuTree(businessUnits) {
  const rootNodeWithChildren = {
    ...businessUnits.filter(bu => bu.level === 1)[0],
    name: 'Default',
    isClientLevelBu: true
  }

  const findChildren = startingBu => {
    const nextLevelOfBusinessUnits = businessUnits.filter(bu => bu.level === startingBu.level + 1)
    const children = nextLevelOfBusinessUnits.filter(bu => startingBu.children.includes(bu.id))
    if (children.length === 0) return []

    return children.map(bu => ({
      id: bu.id,
      name: bu.name,
      isClientLevelBu: false,
      children: findChildren(bu)
    }))
  }

  return [
    {
      ...rootNodeWithChildren,
      children: findChildren(rootNodeWithChildren)
    }
  ]
}
